import { Helmet } from 'react-helmet-async';
import { Graph, WebPage as WebPageSchema } from 'schema-dts';

import { useCanonicalUrl, useLanguageTag } from './hooks';
import type { JsonLdBrand } from './types';
import { jsonLdScriptProp } from './util';

export interface WebPageNodeInput {
  /** Brand di cui parla la pagina (about); se assente la proprietà viene omessa. */
  brand?: JsonLdBrand;
  /** Lingua della pagina come tag BCP 47 (es. "it-IT"); se assente la proprietà viene omessa. */
  inLanguage?: string;
  /** @id dell'entità principale della pagina (mainEntity); se assente la proprietà viene omessa. */
  mainEntityId?: string;
  /** Titolo della pagina. */
  name?: string;
  /** URL canonico della pagina: origin + pathname, senza query string e senza slash finale. */
  url: string;
}

export const getWebPageId = (url: string) => `${url}#webpage`;

/*
  Nodo WebPage con @id {URL}#webpage, collegato al WebSite definito dalla homepage del dominio tramite isPartOf.
  Condiviso tra la scheda negozio e la pagina lista dello store locator, così i criteri restano gli stessi.
*/
export const buildWebPageNode = ({ brand, inLanguage, mainEntityId, name, url }: WebPageNodeInput): WebPageSchema => ({
  '@id': getWebPageId(url),
  '@type': 'WebPage',
  about: brand ? { '@id': brand.id } : undefined,
  inLanguage: inLanguage,
  isPartOf: { '@id': `${new URL(url).origin}/#website` },
  mainEntity: mainEntityId ? { '@id': mainEntityId } : undefined,
  name: name,
  url: url,
});

export type WebPageGraphInput = Omit<WebPageNodeInput, 'mainEntityId'>;

/*
  Grafo JSON-LD con il solo WebPage, senza mainEntity (es. pagina lista dello store locator).
  Funzione pura, così l'output è verificabile senza montare la pagina.
*/
export const buildWebPageGraph = (input: WebPageGraphInput): Graph => ({
  '@context': 'https://schema.org',
  '@graph': [buildWebPageNode(input)],
});

export type WebPageProps = Omit<WebPageGraphInput, 'inLanguage' | 'url'>;

/*
  Emette il solo WebPage della pagina corrente, con URL canonico.
  Chi lo usa deve smontarlo quando la pagina emette già un altro WebPage (es. scheda negozio aperta),
  così sulla pagina resta un solo nodo WebPage.
*/
export const WebPage = ({ brand, name }: WebPageProps) => {
  const url = useCanonicalUrl();
  const inLanguage = useLanguageTag();
  return (
    <Helmet
      script={[jsonLdScriptProp(buildWebPageGraph({ brand: brand, inLanguage: inLanguage, name: name, url: url }))]}
    />
  );
};
